import { hash } from '@ember/helper';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import type { WithBoundArgs } from '@glint/template';

import MetricDetailsTableRowComponent, {
	DEFAULT_SKELETON_COLUMNS,
	type HeaderTimestamp,
} from 'webapp/components/metric-details/table/row';

import type { DataSelect } from 'webapp/models-custom';

interface Args {
	dataSelects: DataSelect[];
	labelColumnTitle?: string;
	summaryColumnTitle?: string;
	summaryColumnOnLeft?: boolean;
	skeletonColumns?: number;
}

interface MetricDetailsTableComponentSignature {
	Args: Args;
	Blocks: {
		body: [
			{
				Row: WithBoundArgs<
					typeof MetricDetailsTableRowComponent,
					'timelinePeriods' | 'skeletonColumns'
				>;
			},
		];
		footer: [];
	};
}

export default class MetricDetailsTableComponent extends Component<MetricDetailsTableComponentSignature> {
	@cached
	get timelinePeriods(): HeaderTimestamp[] {
		for (const dataSelect of this.args.dataSelects) {
			const granulationLabels = dataSelect?.dataRoot?.granulationLabels;
			if (granulationLabels?.length) {
				return granulationLabels.map((gl) => ({ label: gl.label, ts: gl.ts }));
			}
		}
		return [];
	}

	get timelineHeaders(): string[] {
		return this.timelinePeriods.map((period) => period.label);
	}

	get skeletonColumnIndices(): number[] {
		const count = this.args.skeletonColumns ?? DEFAULT_SKELETON_COLUMNS;
		return Array.from({ length: count }, (_, i) => i);
	}

	<template>
		<div class="metric-detail-table-container">
			<div class="metric-detail-table-scroll-wrapper">
				<table
					class="metric-detail-data-table {{if @summaryColumnOnLeft 'summary-on-left'}}"
				>
					<thead>
						<tr>
							<th class="label-column">{{@labelColumnTitle}}</th>
							{{#if @summaryColumnOnLeft}}
								<th class="summary-column">{{@summaryColumnTitle}}</th>
							{{/if}}
							{{#if this.timelineHeaders.length}}
								{{#each this.timelineHeaders as |header|}}
									<th class="data-column">{{header}}</th>
								{{/each}}
							{{else}}
								{{#each this.skeletonColumnIndices}}
									<th class="data-column"></th>
								{{/each}}
							{{/if}}
							{{#unless @summaryColumnOnLeft}}
								<th class="summary-column">{{@summaryColumnTitle}}</th>
							{{/unless}}
						</tr>
					</thead>
					<tbody>
						{{yield
							(hash
								Row=(component
									MetricDetailsTableRowComponent
									timelinePeriods=this.timelinePeriods
									skeletonColumns=@skeletonColumns
									summaryColumnOnLeft=@summaryColumnOnLeft
								)
							)
							to="body"
						}}
					</tbody>
				</table>
			</div>

			{{yield to="footer"}}
		</div>
	</template>
}
